import React, { FC } from 'react';
import {
  Box,
  CircularProgress as ChakraUiCircularProgress,
  CircularProgressLabel as ChakraUiCircularProgressLabel,
  Flex,
  Progress as ChakraUiProgress,
  Spacer,
  Text,
} from '@chakra-ui/react';

type ProgressProps = {
  colorScheme?: string;
  hasStripe?: boolean;
  isAnimated?: boolean;
  max?: number;
  min?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  value?: number;
  variant?: string;
  labelPosition?: 'top' | 'bottom' | 'inline';
  labelText?: string;
};

type CircularProgressProps = {
  thickness?: string; // strokeWeight
  trackColor?: string;
  chakraProgressLabel?: boolean;
  shapeSize?: string | number; // size in px or number
} & Pick<ProgressProps, 'min' | 'max' | 'value' | 'colorScheme'>;

export const Progress: FC<ProgressProps> = ({
  colorScheme,
  hasStripe = false,
  isAnimated = false,
  min = 0,
  max = 100,
  size,
  value,
  variant = 'bar',
  labelPosition = 'top',
  labelText,
  ...rest
}) => {
  /**
   * DOM
   */
  const labelData = (
    <Flex as="header" p={2} gap={2} align={'center'}>
      <Flex gap={2} align={'flex-start'}>
        <Text as="label">{labelText}</Text>
      </Flex>
      <Spacer />
      <Flex gap={2} align={'flex-end'}>
        <Text as="label">{value + '%'}</Text>
      </Flex>
    </Flex>
  );

  if (labelPosition === 'top' || labelPosition === 'bottom') {
    return (
      <Box>
        {labelPosition === 'top' && labelData}
        <ChakraUiProgress
          value={value}
          data-object-type="progress"
          size={size}
          colorScheme={colorScheme}
          hasStripe={hasStripe}
          isAnimated={isAnimated}
          min={min}
          max={max}
          {...rest}
        />
        {labelPosition === 'bottom' && labelData}
      </Box>
    );
  }
  return (
    <Box>
      <Flex gap={1} align="center">
        <ChakraUiProgress
          flex="1 0 auto"
          value={value}
          data-object-type="progress"
          size={size}
          colorScheme={colorScheme}
          hasStripe={hasStripe}
          isAnimated={isAnimated}
          min={min}
          max={max}
          {...rest}
        />
        <Text as="label" align={'right'}>
          {value + '%'}
        </Text>
      </Flex>
    </Box>
  );
};
export const CircularProgress: FC<CircularProgressProps> = ({
  trackColor,
  thickness,
  min = 0,
  max = 100,
  shapeSize,
  value,
  colorScheme,
  chakraProgressLabel = false,
  ...rest
}) => {
  /**
   * DOM
   */
  const label = chakraProgressLabel ? (
    <ChakraUiCircularProgressLabel>{value + '%'}</ChakraUiCircularProgressLabel>
  ) : undefined;
  return (
    <ChakraUiCircularProgress
      data-object-type="progress-circular"
      trackColor={trackColor}
      min={min}
      max={max}
      value={value}
      size={shapeSize}
      thickness={thickness}
      color={`${colorScheme + '.500'}`}
      {...rest}
    >
      {label}
    </ChakraUiCircularProgress>
  );
};
